import { useState, useEffect } from 'react';
import { useDarkMode } from '@/hooks/useDarkMode';
import FloorPlanGenerator from './components/FloorPlanGenerator';
import StyleSelector from './components/StyleSelector';
import DesignCanvas from './components/DesignCanvas';
import CollaborationPanel from './components/CollaborationPanel';
import PresentationMode from './components/PresentationMode';
import ConstructionDocs from './components/ConstructionDocs';
import AIRenderStudio from './components/AIRenderStudio';
import AIVideoStudio from './components/AIVideoStudio';
import SpacePlanner from './components/SpacePlanner';
import SmartWizard from './components/SmartWizard';
import TutorialModal from './components/TutorialModal';
import WhatsNewModal, { SEEN_KEY } from './components/WhatsNewModal';
import KeyboardShortcutsCard from './components/KeyboardShortcutsCard';
import VersionHistoryPanel, { saveSnapshot } from './components/VersionHistoryPanel';
import CostEstimator from './components/CostEstimator';
import CompareLayouts from './components/CompareLayouts';
import ShareDesignModal from './components/ShareDesignModal';
import AIChatAssistant from './components/AIChatAssistant';
import SmartSuggestions from './components/SmartSuggestions';
import NotesPanel from './components/NotesPanel';
import Breadcrumb from '../../components/feature/Breadcrumb';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import LoginModal from '../home/components/LoginModal';
import { useFreeTrialGate } from '../../hooks/useFreeTrialGate';
import UpgradeTrialModal from '../../components/feature/UpgradeTrialModal';

const DEFAULT_FLOOR_PLAN = {
  bedrooms: 3,
  bathrooms: 2,
  livingRooms: 1,
  customRooms: [] as { id: string; name: string; count: number }[],
  area: 2000,
  unit: 'sqft',
  kitchenType: 'open',
  extras: [] as string[],
  prompt: '',
};

const COMPLETED_CHAPTERS_KEY = 'masterArchitectCompletedChapters';
const TOTAL_TUTORIAL_CHAPTERS = 6;

const PRESET_TAGS = ['Client Work', 'Personal', 'Draft', 'Concept', 'Final'];
const TAG_COLORS = ['teal', 'blue', 'purple', 'amber', 'rose', 'gray'];

export default function MasterArchitect() {
  // ── Tab state — uses string to match all tab IDs ──
  const [activeTab, setActiveTab] = useState<string>('floorplan');

  const [darkMode, toggleDarkMode] = useDarkMode();
  const setDarkMode = (v: boolean) => { if (v !== darkMode) toggleDarkMode(); };
  const [floorPlanConfig, setFloorPlanConfig] = useState({ ...DEFAULT_FLOOR_PLAN });
  const [selectedStyle, setSelectedStyle] = useState('');
  const [connectedSoftware, setConnectedSoftware] = useState<string[]>([]);
  const [selectedInteriorStyle, setSelectedInteriorStyle] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('living-room');

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectTags, setProjectTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);
  const [editingTagsProjectId, setEditingTagsProjectId] = useState<string | null>(null);
  const [savedProjects, setSavedProjects] = useState<Array<{
    id: string;
    name: string;
    date: string;
    style: string;
    config: any;
    tags?: string[];
  }>>(() => {
    const saved = localStorage.getItem('masterArchitectProjects');
    return saved ? JSON.parse(saved) : [];
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  const [renameError, setRenameError] = useState(false);

  // New feature states
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showShareDesign, setShowShareDesign] = useState(false);
  const LATEST_VERSION = 'v2.4';
  const [showLogin, setShowLogin] = useState(false);

  const [isComparePinned, setIsComparePinned] = useState(false);

  // ── Generation state — declared early so handleShareDesign can safely reference hasGenerated ──
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);

  // Tutorial progress tracking
  const [completedChaptersCount, setCompletedChaptersCount] = useState(() => {
    try {
      const saved = localStorage.getItem(COMPLETED_CHAPTERS_KEY);
      return saved ? JSON.parse(saved).length : 0;
    } catch {
      return 0;
    }
  });

  // Listen for localStorage changes to update progress badge
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const saved = localStorage.getItem(COMPLETED_CHAPTERS_KEY);
        const count = saved ? JSON.parse(saved).length : 0;
        setCompletedChaptersCount(count);
      } catch {
        setCompletedChaptersCount(0);
      }
    };
    handleStorageChange();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tutorialProgressUpdated', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tutorialProgressUpdated', handleStorageChange);
    };
  }, []);

  // Auto-save snapshot on config/style changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      saveSnapshot(floorPlanConfig, selectedStyle);
    }, 2000);
    return () => clearTimeout(timer);
  }, [floorPlanConfig, selectedStyle]);

  // Auto-show changelog if user hasn't seen the latest version
  useEffect(() => {
    const seen = localStorage.getItem(SEEN_KEY);
    if (seen !== LATEST_VERSION) {
      const timer = setTimeout(() => setShowWhatsNew(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Keyboard shortcut: Ctrl+S → Save, Ctrl+O → Projects, Ctrl+D → Dark mode
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;
      if (e.ctrlKey && e.key === 's') { e.preventDefault(); setShowSaveModal(true); }
      if (e.ctrlKey && e.key === 'o') { e.preventDefault(); setShowProjectsModal(true); }
      if (e.ctrlKey && e.key === 'd') { e.preventDefault(); setDarkMode(!darkMode); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);



  // Listen for recent project load events from DesignCanvas welcome banner
  useEffect(() => {
    const handleRecentProjectLoad = (e: Event) => {
      const project = (e as CustomEvent).detail;
      if (project) handleLoadProject(project);
    };
    window.addEventListener('loadRecentProject', handleRecentProjectLoad);
    return () => window.removeEventListener('loadRecentProject', handleRecentProjectLoad);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedProjects]);

  const toggleTheme = () => setDarkMode(!darkMode);

  const handleResetFloorPlan = () => {
    setFloorPlanConfig({ ...DEFAULT_FLOOR_PLAN });
    setToastMessage('Floor plan reset to default settings.');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleSaveProject = () => {
    if (!projectName.trim()) {
      setToastMessage('Please enter a project name');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }
    const projectData = {
      id: Date.now().toString(),
      name: projectName,
      date: new Date().toISOString(),
      style: selectedStyle,
      tags: projectTags,
      config: {
        floorPlanConfig,
        selectedStyle,
        connectedSoftware,
        selectedInteriorStyle,
        selectedRoom,
        activeTab,
        darkMode,
      },
    };
    const updatedProjects = [...savedProjects, projectData];
    setSavedProjects(updatedProjects);
    localStorage.setItem('masterArchitectProjects', JSON.stringify(updatedProjects));
    setShowSaveModal(false);
    setProjectName('');
    setProjectTags([]);
    setTagInput('');
    setToastMessage('Project saved successfully!');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleLoadProject = (project: any) => {
    setFloorPlanConfig(project.config.floorPlanConfig);
    setSelectedStyle(project.config.selectedStyle);
    setConnectedSoftware(project.config.connectedSoftware);
    setSelectedInteriorStyle(project.config.selectedInteriorStyle);
    setSelectedRoom(project.config.selectedRoom);
    setActiveTab(project.config.activeTab);
    if (project.config.darkMode !== undefined) {
      setDarkMode(project.config.darkMode);
    }
    setShowProjectsModal(false);
    setToastMessage(`Project "${project.name}" loaded successfully!`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleDeleteProject = (projectId: string) => {
    const updatedProjects = savedProjects.filter(p => p.id !== projectId);
    setSavedProjects(updatedProjects);
    localStorage.setItem('masterArchitectProjects', JSON.stringify(updatedProjects));
    setToastMessage('Project deleted successfully');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleWizardComplete = (config: any) => {
    const rooms = config.rooms || [];
    const bedroomRoom = rooms.find((r: any) => r.type === 'Bedroom');
    const bathroomRoom = rooms.find((r: any) => r.type === 'Bathroom');
    setFloorPlanConfig(prev => ({
      ...prev,
      bedrooms: bedroomRoom?.count ?? prev.bedrooms,
      bathrooms: bathroomRoom?.count ?? prev.bathrooms,
      kitchenType: config.kitchenType ?? prev.kitchenType,
      extras: config.extras ?? prev.extras,
      prompt: config.prompt ?? prev.prompt,
    }));
    setShowWizard(false);
    setActiveTab('floorplan');
    setToastMessage('Smart Wizard applied! Your floor plan has been configured.');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleDuplicateProject = (project: any) => {
    const duplicated = {
      ...project,
      id: Date.now().toString(),
      name: `${project.name} (Copy)`,
      date: new Date().toISOString(),
    };
    const updatedProjects = [...savedProjects, duplicated];
    setSavedProjects(updatedProjects);
    localStorage.setItem('masterArchitectProjects', JSON.stringify(updatedProjects));
    setToastMessage(`"${project.name}" duplicated successfully!`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleStartRename = (project: any) => {
    setEditingProjectId(project.id);
    setEditingProjectName(project.name);
    setRenameError(false);
  };

  const handleCancelRename = () => {
    setEditingProjectId(null);
    setEditingProjectName('');
    setRenameError(false);
  };

  const handleSaveRename = (projectId: string) => {
    const trimmedName = editingProjectName.trim();
    if (!trimmedName) {
      setRenameError(true);
      setTimeout(() => setRenameError(false), 500);
      return;
    }
    const updatedProjects = savedProjects.map(p =>
      p.id === projectId ? { ...p, name: trimmedName } : p
    );
    setSavedProjects(updatedProjects);
    localStorage.setItem('masterArchitectProjects', JSON.stringify(updatedProjects));
    setEditingProjectId(null);
    setEditingProjectName('');
    setRenameError(false);
    setToastMessage('Project renamed successfully!');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, projectId: string) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSaveRename(projectId); }
    else if (e.key === 'Escape') { e.preventDefault(); handleCancelRename(); }
  };

  const handleCopyLink = () => {
    const config = { floorPlanConfig, selectedStyle, selectedInteriorStyle, selectedRoom, activeTab };
    const encoded = btoa(encodeURIComponent(JSON.stringify(config)));
    const url = `${window.location.origin}${window.location.pathname}?project=${encoded}`;
    navigator.clipboard.writeText(url).then(() => {
      setToastMessage('Project link copied to clipboard!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setToastMessage('Project link copied to clipboard!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    });
  };

  // handleShareDesign safely references hasGenerated because it is declared above
  const handleShareDesign = () => {
    if (!selectedStyle && !hasGenerated) {
      setToastMessage('Please select a style or generate a design first');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }
    setShowShareDesign(true);
  };

  const handleApplyStyleFromInspiration = (styleName: string) => {
    setSelectedStyle(styleName);
    setActiveTab('style');
    setToastMessage(`${styleName} style applied! Check the House Style tab.`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const { exhausted: trialExhausted, consume: consumeTrial, remaining: trialRemaining, dots: trialDots, used: trialUsed } = useFreeTrialGate('master-architect');
  const [showUpgradeTrial, setShowUpgradeTrial] = useState(false);

  const handleGenerateFloorPlan = () => {
    if (isGenerating) return;
    // Free trial gate
    if (trialExhausted) {
      setShowUpgradeTrial(true);
      return;
    }
    consumeTrial();
    setIsGenerating(true);
    setHasGenerated(false);
    setGenerationProgress(0);

    const steps = [
      'Analyzing floor plan configuration...',
      'Building 3D structural model...',
      'Applying material textures...',
      'Calculating natural lighting...',
      'Rendering exterior views...',
      'Generating environment...',
      'Designing interior spaces...',
      'Creating 360° walkthrough...',
      'Finalizing your design...',
    ];

    let stepIndex = 0;
    setCurrentStep(steps[0]);

    const interval = setInterval(() => {
      setGenerationProgress(prev => {
        const next = prev + Math.floor(Math.random() * 8) + 4;
        const capped = Math.min(next, 100);
        const newStepIndex = Math.floor((capped / 100) * steps.length);
        if (newStepIndex !== stepIndex && newStepIndex < steps.length) {
          stepIndex = newStepIndex;
          setCurrentStep(steps[stepIndex]);
        }
        if (capped >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsGenerating(false);
            setHasGenerated(true);
            setToastMessage('Your design has been generated!');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
          }, 400);
        }
        return capped;
      });
    }, 180);
  };

  const handleRestoreSnapshot = (snapshot: any) => {
    setFloorPlanConfig(snapshot.config);
    setSelectedStyle(snapshot.style);
    setShowVersionHistory(false);
    setToastMessage('Version restored successfully!');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleAddTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !projectTags.includes(trimmedTag)) {
      setProjectTags([...projectTags, trimmedTag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setProjectTags(projectTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(tagInput);
    }
  };

  const handleAddProjectTag = (projectId: string, tag: string) => {
    const updatedProjects = savedProjects.map(p => {
      if (p.id === projectId) {
        const currentTags = p.tags || [];
        if (!currentTags.includes(tag)) return { ...p, tags: [...currentTags, tag] };
      }
      return p;
    });
    setSavedProjects(updatedProjects);
    localStorage.setItem('masterArchitectProjects', JSON.stringify(updatedProjects));
  };

  const handleRemoveProjectTag = (projectId: string, tagToRemove: string) => {
    const updatedProjects = savedProjects.map(p => {
      if (p.id === projectId) {
        return { ...p, tags: (p.tags || []).filter(tag => tag !== tagToRemove) };
      }
      return p;
    });
    setSavedProjects(updatedProjects);
    localStorage.setItem('masterArchitectProjects', JSON.stringify(updatedProjects));
  };

  const getTagColor = (tag: string) => {
    const index = Math.abs(tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % TAG_COLORS.length;
    return TAG_COLORS[index];
  };

  const getTagColorClasses = (color: string, dm: boolean) => {
    const colorMap: { [key: string]: { light: string; dark: string } } = {
      teal:   { light: 'bg-teal-100 text-teal-700 border-teal-200',     dark: 'bg-teal-900/30 text-teal-400 border-teal-700' },
      blue:   { light: 'bg-blue-100 text-blue-700 border-blue-200',     dark: 'bg-blue-900/30 text-blue-400 border-blue-700' },
      purple: { light: 'bg-purple-100 text-purple-700 border-purple-200', dark: 'bg-purple-900/30 text-purple-400 border-purple-700' },
      amber:  { light: 'bg-amber-100 text-amber-700 border-amber-200',  dark: 'bg-amber-900/30 text-amber-400 border-amber-700' },
      rose:   { light: 'bg-rose-100 text-rose-700 border-rose-200',     dark: 'bg-rose-900/30 text-rose-400 border-rose-700' },
      gray:   { light: 'bg-gray-100 text-gray-700 border-gray-200',     dark: 'bg-gray-700 text-gray-300 border-gray-600' },
    };
    return colorMap[color]?.[dm ? 'dark' : 'light'] || colorMap.gray[dm ? 'dark' : 'light'];
  };

  const getAllTags = () => {
    const allTags = new Set<string>();
    savedProjects.forEach(project => { (project.tags || []).forEach(tag => allTags.add(tag)); });
    return Array.from(allTags).sort();
  };

  const getStyleThumbnail = (styleName: string) => {
    const styleImages: { [key: string]: string } = {
      'Colonial':      'https://public.readdy.ai/ai/img_res/3a6824e3a8ce6325611e2c1a19bd5e8d.jpg',
      'Victorian':     'https://readdy.ai/api/search-image?query=ornate%20victorian%20house%20with%20decorative%20trim%20bay%20windows%20turret%20tower%20pastel%20colors%20intricate%20architectural%20details%20beautiful%20garden%20clear%20sky&width=400&height=300&seq=victorian-style-001&orientation=landscape',
      'Craftsman':     'https://readdy.ai/api/search-image?query=craftsman%20style%20bungalow%20with%20exposed%20beams%20wide%20front%20porch%20natural%20wood%20siding%20stone%20accents%20warm%20inviting%20architecture%20beautiful%20landscaping&width=400&height=300&seq=craftsman-style-001&orientation=landscape',
      'Tudor':         'https://readdy.ai/api/search-image?query=tudor%20style%20house%20with%20half%20timbered%20facade%20steeply%20pitched%20roof%20decorative%20woodwork%20brick%20and%20stucco%20exterior%20charming%20english%20architecture%20beautiful%20garden&width=400&height=300&seq=tudor-style-001&orientation=landscape',
      'Minimalist':    'https://readdy.ai/api/search-image?query=minimalist%20modern%20house%20with%20clean%20lines%20white%20walls%20large%20windows%20simple%20geometric%20shapes%20sleek%20contemporary%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=400&height=300&seq=minimalist-style-001&orientation=landscape',
      'Contemporary':  'https://readdy.ai/api/search-image?query=contemporary%20house%20with%20mixed%20materials%20glass%20steel%20wood%20asymmetrical%20design%20innovative%20architecture%20beautiful%20modern%20landscaping%20clear%20blue%20sky&width=400&height=300&seq=contemporary-style-001&orientation=landscape',
      'Industrial':    'https://readdy.ai/api/search-image?query=industrial%20style%20house%20with%20exposed%20concrete%20metal%20accents%20large%20windows%20raw%20materials%20urban%20architecture%20beautiful%20minimal%20landscaping&width=400&height=300&seq=industrial-style-001&orientation=landscape',
      'Scandinavian':  'https://readdy.ai/api/search-image?query=scandinavian%20style%20house%20with%20light%20wood%20facade%20simple%20clean%20design%20large%20windows%20natural%20materials%20nordic%20architecture%20beautiful%20minimal%20garden&width=400&height=300&seq=scandinavian-style-001&orientation=landscape',
      'Mediterranean': 'https://readdy.ai/api/search-image?query=mediterranean%20villa%20with%20terracotta%20roof%20stucco%20walls%20arched%20windows%20courtyard%20warm%20earth%20tones%20beautiful%20landscaping%20palm%20trees%20clear%20sky&width=400&height=300&seq=mediterranean-style-001&orientation=landscape',
      'Farmhouse':     'https://readdy.ai/api/search-image?query=modern%20farmhouse%20with%20white%20board%20and%20batten%20siding%20black%20windows%20metal%20roof%20front%20porch%20rustic%20charm%20beautiful%20landscaping%20clear%20sky&width=400&height=300&seq=farmhouse-style-001&orientation=landscape',
      'Ranch':         'https://readdy.ai/api/search-image?query=ranch%20style%20house%20with%20long%20low%20profile%20attached%20garage%20horizontal%20lines%20simple%20design%20beautiful%20landscaping%20wide%20open%20yard%20clear%20sky&width=400&height=300&seq=ranch-style-001&orientation=landscape',
      'Cape Cod':      'https://readdy.ai/api/search-image?query=cape%20cod%20style%20house%20with%20steep%20roof%20dormer%20windows%20shingle%20siding%20symmetrical%20facade%20traditional%20new%20england%20architecture%20beautiful%20garden&width=400&height=300&seq=capecod-style-001&orientation=landscape',
      'Tiny Home':     'https://readdy.ai/api/search-image?query=modern%20tiny%20house%20with%20efficient%20design%20large%20windows%20wood%20siding%20compact%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky%20sustainable%20living&width=400&height=300&seq=tinyhome-style-001&orientation=landscape',
      'Container':     'https://readdy.ai/api/search-image?query=shipping%20container%20house%20with%20industrial%20design%20large%20windows%20modern%20conversion%20innovative%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=400&height=300&seq=container-style-001&orientation=landscape',
      'A-Frame':       'https://readdy.ai/api/search-image?query=a-frame%20cabin%20with%20steep%20triangular%20roof%20large%20front%20windows%20wood%20siding%20mountain%20retreat%20architecture%20beautiful%20natural%20landscaping%20clear%20sky&width=400&height=300&seq=aframe-style-001&orientation=landscape',
      'Dome':          'https://readdy.ai/api/search-image?query=geodesic%20dome%20house%20with%20unique%20spherical%20design%20modern%20materials%20innovative%20architecture%20beautiful%20landscaping%20clear%20sky%20futuristic%20sustainable%20home&width=400&height=300&seq=dome-style-001&orientation=landscape',
    };
    return styleImages[styleName] || 'https://readdy.ai/api/search-image?query=architectural%20blueprint%20technical%20drawing%20floor%20plan%20grid%20lines%20precise%20measurements%20professional%20design%20clean%20white%20background&width=400&height=300&seq=blueprint-default-001&orientation=landscape';
  };

  const getStyleIcon = (styleName: string) => {
    const styleIcons: { [key: string]: string } = {
      'Colonial': 'ri-building-line', 'Victorian': 'ri-home-heart-line', 'Craftsman': 'ri-home-3-line',
      'Tudor': 'ri-building-line', 'Minimalist': 'ri-layout-line', 'Contemporary': 'ri-building-2-line',
      'Industrial': 'ri-building-4-line', 'Scandinavian': 'ri-home-smile-line', 'Mediterranean': 'ri-sun-line',
      'Farmhouse': 'ri-home-line', 'Ranch': 'ri-home-line', 'Cape Cod': 'ri-home-line',
      'Tiny Home': 'ri-home-line', 'Container': 'ri-archive-line', 'A-Frame': 'ri-home-3-line', 'Dome': 'ri-donut-chart-line',
    };
    return styleIcons[styleName] || 'ri-building-line';
  };

  const filteredProjects = selectedTagFilter
    ? savedProjects.filter(p => (p.tags || []).includes(selectedTagFilter))
    : savedProjects;

  const tabs = [
    { id: 'floorplan',     label: 'Floor Plan',      icon: 'ri-layout-grid-line' },
    { id: 'style',         label: 'House Style',      icon: 'ri-home-smile-line' },
    { id: 'suggestions',   label: 'Smart Suggest',    icon: 'ri-brain-line' },
    { id: 'interior',      label: 'Interior Design',  icon: 'ri-paint-brush-line' },
    { id: 'cost',          label: 'Cost Est.',         icon: 'ri-money-dollar-circle-line' },
    { id: 'chat',          label: 'AI Chat',           icon: 'ri-robot-line' },
    { id: 'notes',         label: 'Notes',             icon: 'ri-sticky-note-line' },
    { id: 'software',      label: 'Software Sync',     icon: 'ri-links-line' },
    { id: 'render',        label: 'AI Render',         icon: 'ri-magic-line' },
    { id: 'video',         label: 'AI Video',          icon: 'ri-video-line' },
    { id: 'space',         label: 'Space Planner',     icon: 'ri-ruler-line' },
    { id: 'collab',        label: 'Collab',            icon: 'ri-team-line', badge: 2 },
    { id: 'presentation',  label: 'Presentation',      icon: 'ri-slideshow-line' },
    { id: 'construction',  label: 'Construction',      icon: 'ri-file-list-line' },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        darkMode 
          ? 'bg-gray-900/95 backdrop-blur-md border-b border-white/10' 
          : 'bg-white/95 backdrop-blur-md border-b border-gray-200'
      }`}>
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-building-line text-xl text-white"></i>
                </div>
                <div>
                  <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Master Architect</h1>
                  <div className="flex items-center gap-2">
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>14 Tools · 1 Platform</p>
                    <span className="px-2 py-0.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs rounded-full font-semibold">
                      openclaw.ai
                    </span>
                  </div>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700">
                <div className="relative">
                  <div className="w-1.5 h-1.5 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-1.5 h-1.5 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full animate-ping opacity-75"></div>
                </div>
                <span className="text-xs font-semibold text-teal-700 dark:text-teal-400">Trained on 2M+ Blueprints</span>
              </div>

              <button
                onClick={() => setShowWizard(true)}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-magic-line"></i>Quick Start Guide
              </button>

              <button
                onClick={() => setShowTutorial(true)}
                className={`px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer relative ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700'}`}
              >
                <i className="ri-play-circle-line"></i>Watch Tutorial
                {completedChaptersCount === 0 ? (
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-bold leading-none">Start</span>
                ) : completedChaptersCount === TOTAL_TUTORIAL_CHAPTERS ? (
                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-bold leading-none flex items-center gap-0.5">
                    <i className="ri-check-line text-xs"></i>Done
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full font-bold leading-none">
                    {completedChaptersCount}/{TOTAL_TUTORIAL_CHAPTERS}
                  </span>
                )}
              </button>

              <button
                onClick={() => setShowWhatsNew(true)}
                className={`px-4 py-3 rounded-xl font-semibold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-teal-400 hover:bg-gray-600' : 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100'
                }`}
              >
                <i className="ri-sparkling-fill"></i>What&apos;s New
                <span className="px-1.5 py-0.5 bg-teal-500 text-white text-xs rounded-full font-bold leading-none">v2.4</span>
              </button>
            </div>

            <div className="flex items-center gap-3">
              <ToolsDropdown isDarkMode={darkMode} />
              
              <button
                onClick={() => setShowSaveModal(true)}
                className={`px-4 py-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-50 text-gray-700'} border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap cursor-pointer`}
              >
                <i className="ri-save-line"></i>
                <span className="text-sm font-medium">Save Project</span>
              </button>

              <button
                onClick={() => setShowProjectsModal(true)}
                className={`px-4 py-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-50 text-gray-700'} border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap relative cursor-pointer`}
              >
                <i className="ri-folder-open-line"></i>
                <span className="text-sm font-medium">My Projects</span>
                {savedProjects.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center">
                    {savedProjects.length}
                  </span>
                )}
              </button>

              <button
                onClick={() => setShowVersionHistory(true)}
                className={`px-4 py-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-50 text-gray-700'} border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap cursor-pointer`}
              >
                <i className="ri-history-line"></i>
                <span className="text-sm font-medium">History</span>
              </button>

              <button
                onClick={toggleTheme}
                className={`p-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' : 'bg-white hover:bg-gray-50 text-gray-700'} border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg transition-colors cursor-pointer`}
              >
                <i className={`${darkMode ? 'ri-sun-line' : 'ri-moon-line'} text-lg`}></i>
              </button>

              <button 
                onClick={() => setShowLogin(true)}
                className={`text-sm font-semibold px-4 py-2 rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  darkMode 
                    ? 'text-white/90 hover:bg-white/10' 
                    : 'border border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                Sign In
              </button>

              <div className="flex items-center rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                <button
                  onClick={handleShareDesign}
                  className={`px-4 py-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-50 text-gray-700'} flex items-center gap-2 transition-colors whitespace-nowrap cursor-pointer border-r ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}
                >
                  <i className="ri-share-line"></i>
                  <span className="text-sm font-medium">Share</span>
                </button>
                <button
                  onClick={handleCopyLink}
                  title="Copy shareable project link"
                  className={`px-3 py-2 ${darkMode ? 'bg-gray-700 hover:bg-teal-700 text-teal-400' : 'bg-white hover:bg-teal-50 text-teal-600'} flex items-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer`}
                >
                  <i className="ri-links-line text-base"></i>
                  <span className="text-xs font-semibold">Copy Link</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="fixed top-16 left-0 right-0 z-40">
        <Breadcrumb currentPage="Master Architect" variant={darkMode ? 'dark' : 'light'} />
      </div>

      {/* Main Content */}
      <div className="pt-32 pb-6 px-4 sm:px-6 lg:px-8">
        <div className="flex h-[calc(100vh-73px)]">
          {/* Left Panel */}
          <div className={`w-96 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r flex flex-col`}>
            {/* Tab Bar */}
            <div className={`border-b transition-colors duration-300 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'} overflow-x-auto`}>
              <div className="flex">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-all duration-300 cursor-pointer ${
                      activeTab === tab.id
                      ? darkMode ? 'text-teal-400 border-b-2 border-teal-400 bg-gray-700/50' : 'text-teal-600 border-b-2 border-teal-500 bg-white'
                      : darkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                    {tab.badge && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">
              {activeTab === 'floorplan' && (
                <FloorPlanGenerator
                  config={floorPlanConfig}
                  onChange={setFloorPlanConfig}
                  darkMode={darkMode}
                  onReset={handleResetFloorPlan}
                  onGenerate={handleGenerateFloorPlan}
                  isGenerating={isGenerating}
                />
              )}
              {activeTab === 'style' && (
                <StyleSelector selectedStyle={selectedStyle} onSelectStyle={setSelectedStyle} darkMode={darkMode} />
              )}
              {activeTab === 'suggestions' && (
                <SmartSuggestions
                  darkMode={darkMode}
                  selectedStyle={selectedStyle}
                  floorPlanConfig={floorPlanConfig}
                  onApplySuggestion={(partial) => setFloorPlanConfig(prev => ({ ...prev, ...partial }))}
                />
              )}
              {activeTab === 'interior' && (
                <div className="p-6 space-y-6">
                  <div>
                    <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Select Room</label>
                    <select
                      value={selectedRoom}
                      onChange={(e) => setSelectedRoom(e.target.value)}
                      className={`w-full px-4 py-2.5 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-teal-500 focus:border-transparent`}
                    >
                      <option value="living-room">Living Room</option>
                      <option value="bedroom">Bedroom</option>
                      <option value="kitchen">Kitchen</option>
                      <option value="bathroom">Bathroom</option>
                      <option value="dining-room">Dining Room</option>
                      <option value="office">Home Office</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Interior Style</label>
                    <div className="grid grid-cols-2 gap-3">
                      {['Scandinavian', 'Japandi', 'Luxury', 'Coastal', 'Industrial', 'Bohemian'].map((style) => (
                        <button
                          key={style}
                          onClick={() => setSelectedInteriorStyle(style)}
                          className={`p-4 rounded-lg border-2 text-left transition-all cursor-pointer ${
                            selectedInteriorStyle === style
                              ? 'border-teal-500 bg-teal-50'
                              : darkMode ? 'border-gray-600 hover:border-gray-500 bg-gray-700' : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          <div className={`text-sm font-medium ${selectedInteriorStyle === style ? 'text-teal-600' : darkMode ? 'text-gray-300' : 'text-gray-900'}`}>{style}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <button className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap">
                    Generate Interior Design
                  </button>
                </div>
              )}
              {activeTab === 'cost' && (
                <CostEstimator
                  darkMode={darkMode}
                  floorPlanConfig={floorPlanConfig}
                  selectedStyle={selectedStyle}
                  onPinCompare={setIsComparePinned}
                  isPinned={isComparePinned}
                />
              )}
              {activeTab === 'chat' && (
                <AIChatAssistant
                  bedrooms={floorPlanConfig.bedrooms}
                  bathrooms={floorPlanConfig.bathrooms}
                  area={floorPlanConfig.area}
                  areaUnit={floorPlanConfig.unit}
                  selectedStyle={selectedStyle}
                  kitchenType={floorPlanConfig.kitchenType}
                  extras={floorPlanConfig.extras}
                />
              )}
              {activeTab === 'notes' && <NotesPanel />}
              {activeTab === 'software' && (
                <div className="p-6 space-y-4">
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Connect your design tools for seamless workflow</p>
                  {[
                    { name: 'Revit', icon: 'ri-building-line' }, { name: 'ArchiCAD', icon: 'ri-home-line' },
                    { name: 'Vectorworks', icon: 'ri-pencil-ruler-line' }, { name: 'SketchUp', icon: 'ri-layout-masonry-line' },
                    { name: 'AutoCAD', icon: 'ri-draft-line' }, { name: 'Planner 5D', icon: 'ri-layout-line' },
                    { name: 'AI House', icon: 'ri-home-smile-line' }, { name: 'Arcadium 3D', icon: 'ri-building-2-line' },
                    { name: 'Maket AI', icon: 'ri-magic-line' }, { name: 'Architechtures', icon: 'ri-building-2-line' },
                    { name: 'Rendair AI', icon: 'ri-image-line' }, { name: 'Nim Video', icon: 'ri-video-line' },
                    { name: 'Qbiq AI', icon: 'ri-ruler-line' }, { name: 'Rerender AI', icon: 'ri-palette-line' },
                  ].map((software) => (
                    <button
                      key={software.name}
                      onClick={() => setConnectedSoftware(prev =>
                        prev.includes(software.name) ? prev.filter(s => s !== software.name) : [...prev, software.name]
                      )}
                      className={`w-full p-4 rounded-lg border-2 flex items-center justify-between transition-all cursor-pointer ${
                        connectedSoftware.includes(software.name)
                          ? 'border-teal-500 bg-teal-50'
                          : darkMode ? 'border-gray-600 hover:border-gray-500 bg-gray-700' : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <i className={`${software.icon} text-xl ${connectedSoftware.includes(software.name) ? 'text-teal-600' : darkMode ? 'text-gray-400' : 'text-gray-600'}`}></i>
                        <span className={`font-medium ${connectedSoftware.includes(software.name) ? 'text-teal-600' : darkMode ? 'text-gray-300' : 'text-gray-900'}`}>{software.name}</span>
                      </div>
                      {connectedSoftware.includes(software.name) && <i className="ri-check-line text-xl text-teal-600"></i>}
                    </button>
                  ))}
                </div>
              )}
              {activeTab === 'render' && <AIRenderStudio darkMode={darkMode} />}
              {activeTab === 'video' && <AIVideoStudio darkMode={darkMode} />}
              {activeTab === 'space' && <SpacePlanner darkMode={darkMode} />}
              {activeTab === 'collab' && <CollaborationPanel darkMode={darkMode} />}
              {activeTab === 'presentation' && <PresentationMode darkMode={darkMode} />}
              {activeTab === 'construction' && <ConstructionDocs darkMode={darkMode} floorPlanConfig={floorPlanConfig} />}
            </div>
          </div>

          {/* Right Panel - Design Canvas */}
          <div className="flex-1 overflow-y-auto">
            <DesignCanvas
              floorPlanConfig={floorPlanConfig}
              selectedStyle={selectedStyle}
              darkMode={darkMode}
              isGenerating={isGenerating}
              progress={generationProgress}
              currentStep={currentStep}
              hasGenerated={hasGenerated}
              onRegenerate={handleGenerateFloorPlan}
              onShareDesign={handleShareDesign}
              onApplyStyle={handleApplyStyleFromInspiration}
              onShowShortcuts={() => setShowShortcuts(prev => !prev)}
            />
          </div>
        </div>

        {/* ── Save Project Modal ── */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-md w-full p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Save Project</h3>
                <button onClick={() => { setShowSaveModal(false); setProjectName(''); setProjectTags([]); setTagInput(''); }} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}>
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="mb-4">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name..."
                  className={`w-full px-4 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent`}
                  autoFocus
                />
              </div>
              <div className="mb-6">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Tags <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(optional)</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {PRESET_TAGS.map(tag => (
                    <button
                      key={tag}
                      onClick={() => handleAddTag(tag)}
                      disabled={projectTags.includes(tag)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                        projectTags.includes(tag)
                          ? darkMode ? 'bg-teal-900/30 text-teal-400 border-teal-700 opacity-50 cursor-not-allowed' : 'bg-teal-100 text-teal-700 border-teal-200 opacity-50 cursor-not-allowed'
                          : darkMode ? 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      <i className="ri-add-line mr-1"></i>{tag}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder="Add custom tag..."
                    className={`flex-1 px-3 py-2 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent`}
                  />
                  <button
                    onClick={() => handleAddTag(tagInput)}
                    disabled={!tagInput.trim()}
                    className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${tagInput.trim() ? 'bg-teal-500 text-white hover:bg-teal-600' : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                {projectTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {projectTags.map(tag => {
                      const color = getTagColor(tag);
                      return (
                        <span key={tag} className={`px-3 py-1.5 text-xs font-medium rounded-full border flex items-center gap-1.5 ${getTagColorClasses(color, darkMode)}`}>
                          {tag}
                          <button onClick={() => handleRemoveTag(tag)} className="hover:opacity-70 transition-opacity cursor-pointer">
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 mb-6`}>
                <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>What will be saved:</h4>
                <ul className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} space-y-1`}>
                  {['Floor plan configuration', 'Selected architectural style', 'Interior design settings', 'Connected software tools', 'Theme preference'].map(item => (
                    <li key={item} className="flex items-center gap-2"><i className="ri-check-line text-teal-500"></i>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowSaveModal(false); setProjectName(''); setProjectTags([]); setTagInput(''); }} className={`flex-1 px-4 py-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer`}>Cancel</button>
                <button onClick={handleSaveProject} className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer">Save Project</button>
              </div>
            </div>
          </div>
        )}

        {/* ── My Projects Modal ── */}
        {showProjectsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col`}>
              <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div>
                  <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>My Projects</h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                    {filteredProjects.length} {selectedTagFilter ? 'filtered' : 'saved'} {filteredProjects.length === 1 ? 'project' : 'projects'}
                  </p>
                </div>
                <button onClick={() => { setShowProjectsModal(false); setSelectedTagFilter(null); }} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}>
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {getAllTags().length > 0 && (
                <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Filter by tag:</span>
                    <button
                      onClick={() => setSelectedTagFilter(null)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${!selectedTagFilter ? 'bg-teal-500 text-white border-teal-500' : darkMode ? 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                    >
                      All Projects
                    </button>
                    {getAllTags().map(tag => {
                      const color = getTagColor(tag);
                      return (
                        <button
                          key={tag}
                          onClick={() => setSelectedTagFilter(tag === selectedTagFilter ? null : tag)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${selectedTagFilter === tag ? 'ring-2 ring-teal-500 ring-offset-1' : ''} ${getTagColorClasses(color, darkMode)}`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-6">
                {filteredProjects.length === 0 ? (
                  <div className="text-center py-12">
                    <div className={`w-16 h-16 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <i className={`ri-folder-open-line text-3xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                    </div>
                    <h4 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>
                      {selectedTagFilter ? `No projects with "${selectedTagFilter}" tag` : 'No saved projects yet'}
                    </h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {selectedTagFilter ? 'Try selecting a different tag filter' : 'Start designing and save your first project!'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredProjects.map((project) => (
                      <div key={project.id} className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-lg overflow-hidden hover:shadow-md transition-all`}>
                        <div className="relative w-full h-40 overflow-hidden">
                          <img src={getStyleThumbnail(project.style)} alt={project.style || 'Project thumbnail'} className="w-full h-full object-cover object-top" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                          {project.style && (
                            <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full flex items-center gap-2 shadow-lg">
                              <i className={`${getStyleIcon(project.style)} text-teal-600 text-sm`}></i>
                              <span className="text-xs font-semibold text-gray-900">{project.style}</span>
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0 pr-2">
                              {editingProjectId === project.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editingProjectName}
                                    onChange={(e) => setEditingProjectName(e.target.value)}
                                    onKeyDown={(e) => handleRenameKeyDown(e, project.id)}
                                    onBlur={() => handleSaveRename(project.id)}
                                    className={`flex-1 px-2 py-1 text-sm font-semibold rounded border-2 ${renameError ? 'border-red-500' : 'border-teal-500'} ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'} focus:outline-none`}
                                    autoFocus
                                  />
                                  <button onClick={() => handleSaveRename(project.id)} className={`w-6 h-6 rounded flex items-center justify-center cursor-pointer ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}><i className="ri-check-line text-sm"></i></button>
                                  <button onClick={handleCancelRename} className={`w-6 h-6 rounded flex items-center justify-center cursor-pointer ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}><i className="ri-close-line text-sm"></i></button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 group">
                                  <h4 className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{project.name}</h4>
                                  <button onClick={() => handleStartRename(project)} className={`w-6 h-6 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer ${darkMode ? 'text-gray-400 hover:text-teal-400' : 'text-gray-400 hover:text-teal-600'}`}><i className="ri-pencil-line text-xs"></i></button>
                                </div>
                              )}
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                {new Date(project.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            </div>
                            <button onClick={() => handleDeleteProject(project.id)} className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}>
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>

                          <div className="mb-3">
                            {editingTagsProjectId === project.id ? (
                              <div className="space-y-2">
                                <div className="flex flex-wrap gap-1.5">
                                  {(project.tags || []).map(tag => {
                                    const color = getTagColor(tag);
                                    return (
                                      <span key={tag} className={`px-2 py-1 text-xs font-medium rounded-full border flex items-center gap-1 ${getTagColorClasses(color, darkMode)}`}>
                                        {tag}
                                        <button onClick={() => handleRemoveProjectTag(project.id, tag)} className="hover:opacity-70 cursor-pointer"><i className="ri-close-line text-xs"></i></button>
                                      </span>
                                    );
                                  })}
                                </div>
                                <div className="flex gap-1.5 flex-wrap">
                                  {PRESET_TAGS.filter(tag => !(project.tags || []).includes(tag)).map(tag => (
                                    <button key={tag} onClick={() => handleAddProjectTag(project.id, tag)} className={`px-2 py-1 text-xs font-medium rounded-full border transition-all cursor-pointer ${darkMode ? 'bg-gray-600 text-gray-400 border-gray-500 hover:bg-gray-500' : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'}`}>
                                      <i className="ri-add-line text-xs"></i>{tag}
                                    </button>
                                  ))}
                                </div>
                                <button onClick={() => setEditingTagsProjectId(null)} className={`text-xs font-medium ${darkMode ? 'text-teal-400' : 'text-teal-600'} hover:underline cursor-pointer`}>Done</button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-wrap">
                                {(project.tags || []).length > 0 ? (
                                  <>
                                    {(project.tags || []).map(tag => {
                                      const color = getTagColor(tag);
                                      return <span key={tag} className={`px-2 py-1 text-xs font-medium rounded-full border ${getTagColorClasses(color, darkMode)}`}>{tag}</span>;
                                    })}
                                    <button onClick={() => setEditingTagsProjectId(project.id)} className={`px-2 py-1 text-xs font-medium rounded-full border transition-all cursor-pointer ${darkMode ? 'bg-gray-600 text-gray-400 border-gray-500 hover:bg-gray-500' : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'}`}>
                                      <i className="ri-price-tag-line text-xs mr-1"></i>Add tags
                                    </button>
                                  </>
                                ) : (
                                  <button onClick={() => setEditingTagsProjectId(project.id)} className={`px-2 py-1 text-xs font-medium rounded-full border transition-all cursor-pointer ${darkMode ? 'bg-gray-600 text-gray-400 border-gray-500 hover:bg-gray-500' : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'}`}>
                                    <i className="ri-price-tag-line text-xs mr-1"></i>Add tags
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 mb-3">
                            <span className={`px-2 py-1 ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-700'} text-xs rounded-full`}>{project.config.floorPlanConfig.bedrooms} bed · {project.config.floorPlanConfig.bathrooms} bath</span>
                            <span className={`px-2 py-1 ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-700'} text-xs rounded-full`}>{project.config.floorPlanConfig.area} {project.config.floorPlanConfig.unit}</span>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleLoadProject(project)} className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer text-sm">Load Project</button>
                            <button onClick={() => handleDuplicateProject(project)} className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-600' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                              <i className="ri-file-copy-line"></i>Duplicate
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Pinned Compare Layouts floating overlay ── */}
        {isComparePinned && (
          <div className="fixed top-36 right-4 z-40 w-80 max-h-[calc(100vh-160px)] overflow-y-auto">
            <div className={`rounded-2xl shadow-2xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <i className={`ri-pushpin-fill text-sm ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}></i>
                  <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Compare Layouts</span>
                </div>
                <button
                  onClick={() => setIsComparePinned(false)}
                  className={`w-6 h-6 flex items-center justify-center rounded-md cursor-pointer transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
              <div className="p-3">
                <CompareLayouts
                  darkMode={darkMode}
                  currentConfig={floorPlanConfig}
                  selectedStyle={selectedStyle}
                />
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        {showWizard && <SmartWizard onComplete={handleWizardComplete} onClose={() => setShowWizard(false)} />}
        {showTutorial && <TutorialModal darkMode={darkMode} onClose={() => setShowTutorial(false)} />}
        {showWhatsNew && <WhatsNewModal darkMode={darkMode} onClose={() => setShowWhatsNew(false)} />}
        {showShortcuts && <KeyboardShortcutsCard darkMode={darkMode} onClose={() => setShowShortcuts(false)} />}

        {/* Floating Help Button */}
        <button
          onClick={() => setShowShortcuts(prev => !prev)}
          title="Keyboard shortcuts"
          className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-lg font-bold transition-all hover:scale-110 cursor-pointer ${
            showShortcuts ? 'bg-teal-500 text-white' : darkMode ? 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <i className={`${showShortcuts ? 'ri-close-line' : 'ri-question-line'} text-xl`}></i>
        </button>

        {showVersionHistory && (
          <VersionHistoryPanel darkMode={darkMode} onClose={() => setShowVersionHistory(false)} onRestore={handleRestoreSnapshot} />
        )}

        {/* Toast */}
        {showToast && (
          <div className="fixed bottom-6 right-20 z-50">
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg px-6 py-4 flex items-center gap-3`}>
              <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-white"></i>
              </div>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{toastMessage}</p>
            </div>
          </div>
        )}

        {showShareDesign && (
          <ShareDesignModal
            darkMode={darkMode}
            onClose={() => setShowShareDesign(false)}
            selectedStyle={selectedStyle}
            floorPlanConfig={floorPlanConfig}
            onCopyLink={handleCopyLink}
          />
        )}

        {/* Login Modal */}
        {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
        {showUpgradeTrial && (
          <UpgradeTrialModal
            toolName="Master Architect"
            usedCount={trialUsed}
            onSignIn={() => { setShowUpgradeTrial(false); setShowLogin(true); }}
            onClose={() => setShowUpgradeTrial(false)}
          />
        )}
      </div>
    </div>
  );
}
